import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Upload, Sparkles, CheckCircle2, ChevronRight, ShieldCheck,
  MapPin, Wifi, Phone, Clock, RefreshCw, FoldVertical,
} from "lucide-react";
import FoxLogo from "@/components/FoxLogo";
import { useCameraCapture } from "@/hooks/useCameraCapture";

import { API } from "@/lib/api";
// Plain axios — public page, no auth cookies, no X-Property-Id header.
const pub = axios.create({ baseURL: API, withCredentials: false });

// Compact per-language string table (5 langs supported, fallback IT).
const I18N = {
  it: {
    welcome: "Benvenuto", you_are_at: "Sei a", from_to: "Dal", to: "al",
    next: "Avanti", back: "Indietro", retake: "Riprova",
    step_doc: "Documento", step_data: "I tuoi dati", step_done: "Fatto",
    intro_title: "Check-in online",
    intro_desc: "In tre passaggi: foto al documento, conferma dei dati, accettazione privacy. Quando arrivi presenti questo telefono al gestore: ha già tutto.",
    start: "Inizia",
    scan_title: "Fotografa la carta d'identità o il passaporto",
    scan_help: "Posiziona il documento in un'area illuminata, dentro al riquadro. Niente flash diretto sulla plastica.",
    capture: "Scatta foto", upload: "Carica file", scanning: "Lettura in corso...",
    scan_button: "Estrai i dati con AI",
    edit_title: "Controlla che i dati siano corretti",
    edit_help: "Puoi correggere a mano qualsiasi campo. Compila email e telefono per ricevere le istruzioni di arrivo.",
    f_first_name: "Nome", f_last_name: "Cognome", f_doc_type: "Tipo documento",
    f_doc_number: "Numero documento", f_nationality: "Nazionalità",
    f_birth_date: "Data di nascita", f_birth_place: "Luogo di nascita",
    f_gender: "Genere", f_email: "Email", f_phone: "Telefono", f_lang: "Lingua",
    privacy_label: "Ho letto e accetto l'informativa privacy (Reg. UE 2016/679).",
    privacy_link: "Leggi l'informativa", submit: "Completa check-in",
    sending: "Invio in corso...",
    done_title: "Check-in completato",
    done_subtitle: "Il gestore confermerà la sua identità all'arrivo.",
    arrival_title: "Tutto pronto per il suo arrivo",
    addr: "Indirizzo", checkin_window: "Orario check-in",
    checkout_time: "Check-out entro", phone: "Telefono", wifi: "Wi-Fi",
    map_open: "Apri in mappe", expired_title: "Link scaduto",
    expired_desc: "Questo link di check-in non è più valido. Contatta direttamente la struttura.",
    invalid_title: "Link non valido",
    invalid_desc: "Il link che hai aperto non esiste o è già stato utilizzato.",
    powered: "Powered by Xenia · The Global Guest Registry",
    must_accept: "Devi accettare l'informativa privacy per continuare",
  },
  en: {
    welcome: "Welcome", you_are_at: "You're at", from_to: "From", to: "to",
    next: "Next", back: "Back", retake: "Retry",
    step_doc: "Document", step_data: "Your details", step_done: "Done",
    intro_title: "Online check-in",
    intro_desc: "Three short steps: snap your ID, confirm details, accept privacy. When you arrive, just show this screen to the host.",
    start: "Start",
    scan_title: "Snap your ID card or passport",
    scan_help: "Place the document in a well-lit area, inside the frame. No direct flash on plastic.",
    capture: "Take photo", upload: "Upload file", scanning: "Reading...",
    scan_button: "Extract data with AI",
    edit_title: "Please review your details",
    edit_help: "Edit any field manually. Add email and phone to receive arrival instructions.",
    f_first_name: "First name", f_last_name: "Last name", f_doc_type: "Document type",
    f_doc_number: "Document number", f_nationality: "Nationality",
    f_birth_date: "Date of birth", f_birth_place: "Place of birth",
    f_gender: "Gender", f_email: "Email", f_phone: "Phone", f_lang: "Language",
    privacy_label: "I have read and accept the privacy notice (EU Reg. 2016/679).",
    privacy_link: "Read notice", submit: "Complete check-in",
    sending: "Sending...",
    done_title: "Check-in completed",
    done_subtitle: "The host will verify your identity when you arrive.",
    arrival_title: "You're all set for arrival",
    addr: "Address", checkin_window: "Check-in window",
    checkout_time: "Check-out by", phone: "Phone", wifi: "Wi-Fi",
    map_open: "Open in maps", expired_title: "Link expired",
    expired_desc: "This check-in link is no longer valid. Contact the property directly.",
    invalid_title: "Invalid link",
    invalid_desc: "The link you opened does not exist or has already been used.",
    powered: "Powered by Xenia · The Global Guest Registry",
    must_accept: "You must accept the privacy notice to continue",
  },
  fr: {
    welcome: "Bienvenue", you_are_at: "Vous êtes à", from_to: "Du", to: "au",
    next: "Suivant", back: "Retour", retake: "Réessayer",
    step_doc: "Document", step_data: "Vos informations", step_done: "Terminé",
    intro_title: "Enregistrement en ligne",
    intro_desc: "En trois étapes : photo de la pièce, vérification des données, acceptation de la politique. À l'arrivée, montrez cet écran.",
    start: "Commencer",
    scan_title: "Photographiez votre pièce d'identité ou votre passeport",
    scan_help: "Placez le document dans une zone bien éclairée, à l'intérieur du cadre.",
    capture: "Photo", upload: "Charger", scanning: "Lecture...",
    scan_button: "Extraire avec l'IA",
    edit_title: "Vérifiez vos informations",
    edit_help: "Vous pouvez modifier chaque champ. Ajoutez email et téléphone pour recevoir les instructions.",
    f_first_name: "Prénom", f_last_name: "Nom", f_doc_type: "Type",
    f_doc_number: "Numéro", f_nationality: "Nationalité",
    f_birth_date: "Naissance", f_birth_place: "Lieu de naissance",
    f_gender: "Sexe", f_email: "Email", f_phone: "Téléphone", f_lang: "Langue",
    privacy_label: "J'accepte la politique de confidentialité (Règl. UE 2016/679).",
    privacy_link: "Lire la notice", submit: "Terminer",
    sending: "Envoi...",
    done_title: "Check-in terminé",
    done_subtitle: "L'hôte vérifiera votre identité à l'arrivée.",
    arrival_title: "Tout est prêt pour votre arrivée",
    addr: "Adresse", checkin_window: "Horaires d'arrivée",
    checkout_time: "Départ avant", phone: "Téléphone", wifi: "Wi-Fi",
    map_open: "Ouvrir la carte", expired_title: "Lien expiré",
    expired_desc: "Ce lien n'est plus valide. Contactez directement l'établissement.",
    invalid_title: "Lien invalide",
    invalid_desc: "Le lien ouvert n'existe pas.",
    powered: "Propulsé par Xenia · The Global Guest Registry",
    must_accept: "Veuillez accepter la politique pour continuer",
  },
  de: {
    welcome: "Willkommen", you_are_at: "Sie sind im", from_to: "Vom", to: "bis",
    next: "Weiter", back: "Zurück", retake: "Wiederholen",
    step_doc: "Dokument", step_data: "Ihre Daten", step_done: "Fertig",
    intro_title: "Online-Check-in",
    intro_desc: "Drei kurze Schritte: Ausweis fotografieren, Daten prüfen, Datenschutz akzeptieren. Bei Ankunft zeigen Sie diesen Bildschirm.",
    start: "Starten",
    scan_title: "Ausweis oder Reisepass fotografieren",
    scan_help: "Im hellen Bereich, innerhalb des Rahmens. Kein direkter Blitz.",
    capture: "Foto", upload: "Hochladen", scanning: "Lesen...",
    scan_button: "Mit KI auslesen",
    edit_title: "Bitte Ihre Daten prüfen",
    edit_help: "Sie können jedes Feld manuell bearbeiten.",
    f_first_name: "Vorname", f_last_name: "Nachname", f_doc_type: "Dokumenttyp",
    f_doc_number: "Nummer", f_nationality: "Nationalität",
    f_birth_date: "Geburtsdatum", f_birth_place: "Geburtsort",
    f_gender: "Geschlecht", f_email: "E-Mail", f_phone: "Telefon", f_lang: "Sprache",
    privacy_label: "Ich akzeptiere die Datenschutzerklärung (EU 2016/679).",
    privacy_link: "Datenschutz lesen", submit: "Check-in abschließen",
    sending: "Senden...",
    done_title: "Check-in abgeschlossen",
    done_subtitle: "Der Gastgeber prüft Ihre Identität bei Ankunft.",
    arrival_title: "Alles bereit für Ihre Ankunft",
    addr: "Adresse", checkin_window: "Check-in-Zeit",
    checkout_time: "Check-out bis", phone: "Telefon", wifi: "WLAN",
    map_open: "In Karten öffnen", expired_title: "Link abgelaufen",
    expired_desc: "Dieser Link ist nicht mehr gültig.",
    invalid_title: "Ungültiger Link",
    invalid_desc: "Dieser Link existiert nicht.",
    powered: "Powered by Xenia · The Global Guest Registry",
    must_accept: "Sie müssen die Datenschutzerklärung akzeptieren",
  },
  zh: {
    welcome: "欢迎", you_are_at: "您将入住", from_to: "从", to: "至",
    next: "下一步", back: "返回", retake: "重试",
    step_doc: "证件", step_data: "您的信息", step_done: "完成",
    intro_title: "在线办理入住",
    intro_desc: "三个简单步骤：拍摄证件、确认信息、接受隐私条款。到达时只需向房东出示此页面。",
    start: "开始",
    scan_title: "拍摄您的身份证或护照",
    scan_help: "请在光线良好的区域、将证件放入框内。",
    capture: "拍照", upload: "上传", scanning: "读取中...",
    scan_button: "AI 提取数据",
    edit_title: "请核对您的信息",
    edit_help: "您可以手动修改任何字段。",
    f_first_name: "名字", f_last_name: "姓氏", f_doc_type: "证件类型",
    f_doc_number: "证件号码", f_nationality: "国籍",
    f_birth_date: "出生日期", f_birth_place: "出生地",
    f_gender: "性别", f_email: "电子邮件", f_phone: "电话", f_lang: "语言",
    privacy_label: "我已阅读并接受隐私政策（欧盟 2016/679）。",
    privacy_link: "阅读隐私政策", submit: "完成入住",
    sending: "发送中...",
    done_title: "办理完成",
    done_subtitle: "房东将在您到达时核实身份。",
    arrival_title: "一切就绪",
    addr: "地址", checkin_window: "入住时间",
    checkout_time: "退房时间", phone: "电话", wifi: "Wi-Fi",
    map_open: "打开地图", expired_title: "链接已过期",
    expired_desc: "此链接已失效。",
    invalid_title: "无效链接",
    invalid_desc: "此链接不存在。",
    powered: "由 Xenia · The Global Guest Registry 提供支持",
    must_accept: "请接受隐私政策后继续",
  },
};

const LANG_OPTIONS = [
  { code: "it", label: "Italiano" }, { code: "en", label: "English" },
  { code: "fr", label: "Français" }, { code: "de", label: "Deutsch" },
  { code: "zh", label: "中文" },
];

export default function SelfCheckin() {
  const { token } = useParams();
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("intro"); // intro | scan | review | done
  const [lang, setLang] = useState("it");
  const [imgB64, setImgB64] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    first_name: "", last_name: "", document_type: "carta_identita",
    document_number: "", nationality: "ITA", birth_date: "", birth_place: "",
    gender: "", email: "", phone: "", language: "it", privacy_accepted: false,
  });
  const { videoRef, streaming, startCamera, stopCamera, capture: captureFrame } = useCameraCapture();

  const T = I18N[lang] || I18N.it;

  // Load welcome bundle
  useEffect(() => {
    let active = true;
    pub.get(`/selfcheckin/${token}`)
      .then(({ data: b }) => {
        if (!active) return;
        setBundle(b);
        const guessed = (b.language || "it").toLowerCase();
        if (LANG_OPTIONS.find(l => l.code === guessed)) setLang(guessed);
        setData(d => ({ ...d, first_name: b.first_name || "", language: guessed }));
        if (b.status === "completed" || b.status === "verified") setStep("done");
      })
      .catch(err => {
        if (!active) return;
        const code = err?.response?.status;
        setError(code === 410 ? "expired" : "invalid");
      });
    return () => { active = false; };
  }, [token]);

  const capture = useCallback(() => {
    const out = captureFrame();
    if (!out) return;
    setImgPreview(out.dataUrl);
    setImgB64(out.base64);
  }, [captureFrame]);

  const onFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      setImgPreview(r);
      const i = r.indexOf(",");
      setImgB64(i >= 0 ? r.slice(i + 1) : r);
    };
    reader.readAsDataURL(file);
  }, []);

  const runScan = useCallback(async () => {
    if (!imgB64) return;
    setScanning(true);
    try {
      const { data: out } = await pub.post(`/selfcheckin/${token}/scan`, { image_base64: imgB64 });
      setData(d => ({
        ...d,
        first_name: out.first_name || d.first_name,
        last_name: out.last_name || d.last_name,
        document_type: out.document_type || d.document_type,
        document_number: out.document_number || d.document_number,
        nationality: out.nationality || d.nationality,
        birth_date: out.birth_date || d.birth_date,
        birth_place: out.birth_place || d.birth_place,
        gender: out.gender || d.gender,
      }));
      setStep("review");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore scansione");
    } finally { setScanning(false); }
  }, [imgB64, token]);

  const submit = useCallback(async () => {
    if (!data.privacy_accepted) { toast.error(T.must_accept); return; }
    setSubmitting(true);
    try {
      const payload = { ...data, language: lang };
      // Strip empty optional date strings (Pydantic accepts null but not "")
      ["birth_date", "birth_place", "issue_date", "expiry_date", "issuing_authority", "gender", "email", "phone"]
        .forEach(k => { if (!payload[k]) delete payload[k]; });
      await pub.post(`/selfcheckin/${token}/submit`, payload);
      setStep("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSubmitting(false); }
  }, [data, lang, token, T]);

  // ---------- error states ----------
  if (error) {
    const isExpired = error === "expired";
    return (
      <Shell lang={lang} setLang={setLang}>
        <div className="text-center py-16" data-testid="selfcheckin-error">
          <FoldVertical className="w-16 h-16 mx-auto mb-6 text-slate-400" strokeWidth={1.2} />
          <h1 className="font-heading font-semibold text-3xl mb-3">{isExpired ? T.expired_title : T.invalid_title}</h1>
          <p className="text-slate-600">{isExpired ? T.expired_desc : T.invalid_desc}</p>
        </div>
      </Shell>
    );
  }

  if (!bundle) {
    return <Shell lang={lang} setLang={setLang}><div className="text-slate-500 text-sm py-12">…</div></Shell>;
  }

  const propAddress = bundle.property?.address;
  const mapsUrl = propAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(propAddress)}`
    : null;

  return (
    <Shell lang={lang} setLang={setLang}>
      <Stepper step={step} T={T} />

      {step === "intro" && (
        <div data-testid="selfcheckin-intro">
          <p className="text-xs tracked text-blue-600 mb-3">{T.welcome.toUpperCase()}</p>
          <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-2">
            {bundle.first_name ? `${T.welcome}, ${bundle.first_name}.` : T.welcome}
          </h1>
          <p className="text-slate-600 text-lg mb-1">
            {T.you_are_at}: <strong className="text-slate-900">{bundle.property.name}</strong>
          </p>
          <p className="text-slate-500 mb-8">
            {T.from_to} <strong>{bundle.stay.check_in}</strong> {T.to} <strong>{bundle.stay.check_out}</strong>
          </p>
          <h2 className="font-heading font-semibold text-2xl mb-2">{T.intro_title}</h2>
          <p className="text-slate-600 max-w-prose mb-8">{T.intro_desc}</p>
          <button onClick={() => setStep("scan")} data-testid="selfcheckin-start"
            className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-6 py-3 text-sm rounded-lg hover:bg-stone-800">
            {T.start} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {step === "scan" && (
        <div data-testid="selfcheckin-scan">
          <h2 className="font-heading font-semibold text-3xl mb-2">{T.scan_title}</h2>
          <p className="text-slate-600 mb-6">{T.scan_help}</p>

          <div className="aspect-[4/3] bg-stone-900 border border-dashed border-slate-300 relative overflow-hidden mb-4 flex items-center justify-center">
            {imgPreview ? (
              <img src={imgPreview} alt="doc" className="max-w-full max-h-full object-contain" />
            ) : streaming ? (
              <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
            ) : (
              <Camera className="w-16 h-16 text-slate-500" strokeWidth={1} />
            )}
            {!imgPreview && streaming && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-[85%] h-[65%] border-2 border-blue-500/80 rounded-lg" />
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {!imgPreview && !streaming && (
              <>
                <button onClick={startCamera} data-testid="selfcheckin-start-camera"
                  className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-4 py-2 text-sm rounded-lg">
                  <Camera className="w-4 h-4" /> {T.capture}
                </button>
                <label className="inline-flex items-center gap-2 cursor-pointer border border-slate-300 px-4 py-2 text-sm">
                  <Upload className="w-4 h-4" /> {T.upload}
                  <input type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="selfcheckin-upload" />
                </label>
              </>
            )}
            {!imgPreview && streaming && (
              <button onClick={capture} data-testid="selfcheckin-capture"
                className="inline-flex items-center gap-2 bg-blue-600 text-stone-50 px-5 py-2 text-sm rounded-lg">
                <Camera className="w-4 h-4" /> {T.capture}
              </button>
            )}
            {imgPreview && (
              <>
                <button onClick={runScan} disabled={scanning} data-testid="selfcheckin-do-scan"
                  className="inline-flex items-center gap-2 bg-blue-600 text-stone-50 px-5 py-2 text-sm rounded-lg disabled:opacity-60">
                  <Sparkles className="w-4 h-4" /> {scanning ? T.scanning : T.scan_button}
                </button>
                <button onClick={() => { setImgB64(null); setImgPreview(null); }}
                  className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm">
                  <RefreshCw className="w-4 h-4" /> {T.retake}
                </button>
              </>
            )}
            <button onClick={() => setStep("intro")} className="ml-auto text-sm text-slate-500 underline">
              {T.back}
            </button>
          </div>
        </div>
      )}

      {step === "review" && (
        <div data-testid="selfcheckin-review">
          <h2 className="font-heading font-semibold text-3xl mb-2">{T.edit_title}</h2>
          <p className="text-slate-600 mb-6">{T.edit_help}</p>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label={T.f_first_name} value={data.first_name} onChange={v => setData({ ...data, first_name: v })} testid="sc-first-name" />
            <Field label={T.f_last_name} value={data.last_name} onChange={v => setData({ ...data, last_name: v })} testid="sc-last-name" />
            <Field label={T.f_doc_type} value={data.document_type} onChange={v => setData({ ...data, document_type: v })} testid="sc-doc-type" />
            <Field label={T.f_doc_number} value={data.document_number} onChange={v => setData({ ...data, document_number: v })} testid="sc-doc-number" />
            <Field label={T.f_nationality} value={data.nationality} onChange={v => setData({ ...data, nationality: v })} testid="sc-nationality" />
            <Field label={T.f_birth_date} type="date" value={data.birth_date} onChange={v => setData({ ...data, birth_date: v })} testid="sc-birth-date" />
            <Field label={T.f_birth_place} value={data.birth_place} onChange={v => setData({ ...data, birth_place: v })} testid="sc-birth-place" />
            <SelectField label={T.f_gender} value={data.gender} onChange={v => setData({ ...data, gender: v })}
              options={[["", "—"], ["M", "M"], ["F", "F"], ["X", "X"]]} testid="sc-gender" />
            <Field label={T.f_email} type="email" value={data.email} onChange={v => setData({ ...data, email: v })} testid="sc-email" />
            <Field label={T.f_phone} type="tel" value={data.phone} onChange={v => setData({ ...data, phone: v })} testid="sc-phone" />
          </div>

          <label className="flex items-start gap-3 mt-8 cursor-pointer" data-testid="sc-privacy-label">
            <input type="checkbox" checked={data.privacy_accepted}
              onChange={e => setData({ ...data, privacy_accepted: e.target.checked })}
              className="mt-1 accent-blue-700 w-4 h-4" data-testid="sc-privacy-checkbox" />
            <span className="text-sm text-slate-700">
              <ShieldCheck className="w-4 h-4 inline mr-1 text-blue-600" />
              {T.privacy_label}{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-blue-600">
                {T.privacy_link}
              </a>
            </span>
          </label>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setStep("scan")}
              className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm">
              {T.back}
            </button>
            <button onClick={submit} disabled={submitting} data-testid="selfcheckin-submit"
              className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-6 py-2.5 text-sm rounded-lg hover:bg-stone-800 disabled:opacity-60">
              <CheckCircle2 className="w-4 h-4" /> {submitting ? T.sending : T.submit}
            </button>
          </div>
        </div>
      )}

      {step === "done" && (
        <div data-testid="selfcheckin-done">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 border border-green-200 mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-700" />
          </div>
          <h1 className="font-heading font-semibold text-4xl mb-2">{T.done_title}</h1>
          <p className="text-slate-600 mb-10">{T.done_subtitle}</p>

          <h2 className="font-heading font-semibold text-2xl mb-4">{T.arrival_title}</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            {bundle.property.address && (
              <InfoRow icon={MapPin} label={T.addr} value={bundle.property.address} testid="info-address">
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs ml-2">
                    {T.map_open}
                  </a>
                )}
              </InfoRow>
            )}
            {bundle.property.checkin_window && (
              <InfoRow icon={Clock} label={T.checkin_window} value={bundle.property.checkin_window} testid="info-checkin-window" />
            )}
            {bundle.property.checkout_time && (
              <InfoRow icon={Clock} label={T.checkout_time} value={bundle.property.checkout_time} testid="info-checkout-time" />
            )}
            {bundle.property.phone && (
              <InfoRow icon={Phone} label={T.phone} value={bundle.property.phone} testid="info-phone">
                <a href={`tel:${bundle.property.phone}`} className="text-blue-600 underline text-xs ml-2">→</a>
              </InfoRow>
            )}
            {bundle.property.wifi_ssid && (
              <InfoRow icon={Wifi} label={T.wifi}
                value={`${bundle.property.wifi_ssid}${bundle.property.wifi_password ? " · " + bundle.property.wifi_password : ""}`}
                testid="info-wifi" />
            )}
          </div>
        </div>
      )}
    </Shell>
  );
}

// ---------------- helper components ----------------
function Shell({ lang, setLang, children }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <FoxLogo className="w-7 h-7" />
            <span className="font-heading font-semibold text-lg">Xenia<span className="text-blue-600">.</span></span>
          </div>
          <select value={lang} onChange={e => setLang(e.target.value)}
            className="text-xs bg-transparent border border-slate-200 rounded-lg px-2 py-1"
            data-testid="selfcheckin-lang">
            {LANG_OPTIONS.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-10 md:py-16">{children}</main>
      <footer className="max-w-3xl mx-auto px-5 py-8 text-xs text-slate-400 border-t border-slate-200 mt-12">
        {(I18N[lang] || I18N.it).powered}
      </footer>
    </div>
  );
}

function Stepper({ step, T }) {
  const steps = [
    { id: "scan", label: T.step_doc },
    { id: "review", label: T.step_data },
    { id: "done", label: T.step_done },
  ];
  const idx = steps.findIndex(s => s.id === step);
  if (step === "intro") return null;
  return (
    <div className="flex items-center gap-2 mb-10 text-xs uppercase tracking-widest text-slate-500" data-testid="selfcheckin-stepper">
      {steps.map((s, i) => (
        <div key={s.id} className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${i <= idx ? "bg-blue-600 border-blue-700 text-white" : "border-slate-300 text-slate-400"}`}>
            {i + 1}
          </span>
          <span className={i <= idx ? "text-slate-900" : ""}>{s.label}</span>
          {i < steps.length - 1 && <span className="w-8 border-t border-slate-300 mx-1" />}
        </div>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500">{label}</span>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full mt-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white"
        data-testid={testid} />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500">{label}</span>
      <select value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full mt-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white"
        data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function InfoRow({ icon: Icon, label, value, testid, children }) {
  return (
    <div className="border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-xs text-slate-500 flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-medium text-slate-900">
        {value}{children}
      </div>
    </div>
  );
}
